import React, { useEffect, useMemo, useState } from "react";
import { Box, Chip } from "@mui/material";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { withStyles } from "@material-ui/core";
import LinearProgress from "@material-ui/core/LinearProgress";
import {
  AutoSizer,
  List,
  CellMeasurer,
  CellMeasurerCache,
} from "react-virtualized";
import Markdown from "react-markdown";

const SocketLogs = ({ socketLogs, progress, handleViewData, selectedName }) => {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const filteredLogs = useMemo(() => {
    return socketLogs.filter(
      (log) =>
        (log?.company?.toLowerCase().includes(search?.toLowerCase()) ||
          log?.job?.name?.toLowerCase().includes(search?.toLowerCase())) &&
        (statusFilter === ""
          ? true
          : statusFilter === "success"
          ? log?.result?.success
          : !log?.result?.success)
    );
  }, [search, socketLogs, statusFilter]);

  useEffect(() => {
    if (selectedName && selectedName != "") {
      setSearch(selectedName);
    }
  }, [selectedName]);

  const handleFilterClick = (status) => {
    setStatusFilter((prev) => (prev === status ? "" : status));
  };

  const StyledLinearProgress = withStyles({
    root: {
      height: 10,
      borderRadius: 5,
      backgroundColor: darkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
    },
    bar: {
      borderRadius: 5,
      backgroundColor: darkMode ? "#90caf9" : "#1976d2",
    },
  })(LinearProgress);

  // Create a cache for cell measurements
  const cache = new CellMeasurerCache({
    fixedWidth: true,
    minHeight: 100,
  });

  const renderRow = ({ index, key, parent, style }) => {
    const log = filteredLogs[index];
    return (
      <CellMeasurer
        cache={cache}
        columnIndex={0}
        key={key}
        parent={parent}
        rowIndex={index}
      >
        {({ measure }) => (
          <Box
            style={{
              ...style,
              marginBottom: "16px",
              padding: "16px",
              border: "1px solid #eee",
              borderRadius: "8px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              transition: "box-shadow 0.3s ease",
            }}
            onLoad={measure}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                justifyContent: "space-between",
                alignItems: { xs: "flex-start", sm: "center" },
                marginBottom: "16px",
                gap: "8px",
              }}
            >
              <MDTypography
                variant="h6"
                sx={{ wordBreak: "break-word", mr: { sm: 2 } }}
              >
                {log.company} {log.job.name ? `- ${log.job.name}` : ""}
              </MDTypography>

              <Chip
                label={
                  log?.stage === "FILTER_STAGE" && !log?.result?.success
                    ? "Ignore"
                    : log?.result?.success
                    ? "Success"
                    : "Failure"
                }
                color={
                  log?.stage === "FILTER_STAGE" && !log?.result?.success
                    ? "default"
                    : log?.result?.success
                    ? "info"
                    : "error"
                }
                size="small"
                sx={{
                  minWidth: "80px",
                  justifyContent: "center",
                }}
              />
            </Box>
            <MDTypography variant="body2" sx={{ mb: 1 }}>
              <strong>Stage:</strong> {log?.stage}
            </MDTypography>
            <MDTypography variant="body2" sx={{ mb: 1 }}>
              <strong>Message:</strong>{" "}
              <Markdown>{log?.result?.message}</Markdown>
            </MDTypography>
            <MDTypography variant="body2" sx={{ mb: 2 }}>
              <strong>Date:</strong> {new Date(log?.createdAt).toLocaleString()}
            </MDTypography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {(log?.result?.sanitizeData || log?.result?.content) && (
                <MDButton
                  variant="outlined"
                  color="info"
                  size="small"
                  onClick={() =>
                    handleViewData({
                      content: log?.result?.content,
                      sanitizeData: log?.result?.sanitizeData,
                    })
                  }
                >
                  View Sanitize Data
                </MDButton>
              )}
            </Box>
          </Box>
        )}
      </CellMeasurer>
    );
  };

  return (
    <Box
      sx={{
        border: "1px solid #ccc",
        borderRadius: "8px",
        height: { xs: "50vh", md: "calc(100vh - 220px)" },
        background: `${darkMode ? "#202940" : "#FFF"}`,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          p: 2,
          borderBottom: "1px solid #ccc",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            gap: 2,
          }}
        >
          <MDTypography variant="h2" sx={{ flexShrink: 0 }}>
            Socket Logs
          </MDTypography>
          <MDInput
            placeholder="Search..."
            value={search}
            size="small"
            fullWidth
            sx={{ maxWidth: { sm: "300px" } }}
            onChange={({ currentTarget }) => {
              setSearch(currentTarget.value);
            }}
          />
        </Box>
        <Box sx={{ mt: 2 }}>
          <Box
            sx={{
              mt: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              "& > *:not(:last-child)": {
                borderRight: "1px solid",
                pr: 2, // Padding-right for spacing after the pipe
              },
            }}
          >
            <MDTypography variant="body2" sx={{ mb: 1 }}>
              Progress: {progress?.progressPercentage || 0}%
            </MDTypography>
            <MDTypography variant="body2" sx={{ mb: 1 }}>
              Done : {progress?.done || 0} / {progress?.total || 0}
            </MDTypography>
            <MDTypography
              variant="body2"
              sx={{ mb: 1, cursor: 'pointer', borderBottom: statusFilter === 'success' ? '2px solid' : '' }}
              onClick={() => handleFilterClick("success")}
            >
              Success : {progress?.success || 0}
            </MDTypography>
            <MDTypography
              variant="body2"
              sx={{ mb: 1, cursor: 'pointer', borderBottom: statusFilter === 'failed' ? '2px solid' : '' }}
              onClick={() => handleFilterClick("failed")}
            >
              Failed : {progress?.failed || 0}
            </MDTypography>
          </Box>
          <StyledLinearProgress
            variant="determinate"
            value={(progress?.progressPercentage || 0)?.toFixed(2)}
          />
        </Box>
      </Box>

      <Box sx={{ flexGrow: 1, overflowY: "auto", p: 2 }}>
        {filteredLogs.length > 0 ? (
          <AutoSizer>
            {({ width, height }) => (
              <List
                width={width}
                height={height}
                rowCount={filteredLogs.length}
                rowHeight={cache.rowHeight}
                deferredMeasurementCache={cache}
                rowRenderer={renderRow}
                overscanRowCount={5}
              />
            )}
          </AutoSizer>
        ) : (
          <MDTypography variant="h6" gutterBottom>
            No logs available.
          </MDTypography>
        )}
      </Box>
    </Box>
  );
};

export default SocketLogs;
